import React, { Component } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Icon, Header, Grid, Divider, Popup, Flag } from 'semantic-ui-react';
import { colors } from 'utils/colors';
import { ACCOUNTS_ANALYZE, ACCOUNTS_ADDITIONAL_FEATURES } from 'utils/features';
import { Accounts, Portfolio, Storage, ReallocateAccount } from 'actions';
import { Segment, Button, Loading, Feature, ButtonFA } from 'components';
import { faSync } from '@fortawesome/free-solid-svg-icons';

import cn from 'classnames';
import { HoldingTable, GicTable, AssetChart } from './components';
import {
  getHoldTotalMarketValue,
  hasNegativeWeight,
} from '../../../utils/helpers';

class Overview extends Component {
  componentWillMount() {
    const { dispatch, overview } = this.props;

    if (!overview) {
      return dispatch(Accounts.fetchAll());
    }

    return false;
  }

  componentWillUnmount() {
    const { dispatch, history } = this.props;

    if (
      history.location.pathname !== '/accounts/user' &&
      history.location.pathname !== '/portfolio/unsaved/overview'
    ) {
      dispatch(Accounts.saveSingleAccInfo(''));
    }
  }

  parseNum = (num) => {
    num = !num ? 0 : Math.round(num * 100) / 100;
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  analyze = (holdings) => {
    const { dispatch, history } = this.props;
    const gicHoldings = holdings.filter((holding) => holding.gic_ticker);
    Storage.removeItem('pwpa-has-exclude-holding');

    return dispatch(
      Accounts.analyze(
        holdings,
        history,
        { user_institution: 'Overview; All Accounts' },
        gicHoldings
      )
    );
  };

  render() {
    const {
      dispatch,
      overview,
      history,
      accountList,
      pwpaExchangeRate,
      user_region,
    } = this.props;
    const excludeGicHoldings =
      overview && overview.holdings.filter((holding) => !holding.gic_ticker);
    const gicHoldings =
      overview && overview.holdings.filter((holding) => holding.gic_ticker);
    const hasNegativeWeights = hasNegativeWeight(overview.holdings);
    const isNegativePort =
      overview &&
      getHoldTotalMarketValue(overview, pwpaExchangeRate, user_region) < 0;

    return !overview ? (
      <Loading active />
    ) : (
      <Segment>
        <Header
          size="large"
          content="Overview"
          subheader="Key portfolio information"
        />
        <Feature name={ACCOUNTS_ANALYZE}>
          <CurrencyOption>
            <Button
              className={cn('left', {
                disabled: isNegativePort,
              })}
              icon="line chart"
              color={colors.darkBlue}
              onClick={() => {
                this.analyze(overview.holdings);
                dispatch(Portfolio.notSaveUrlSc());
              }}
              style={{
                zIndex: 1,
              }}
            >
              Analyze
            </Button>
            {overview.cash > 0 && (
              <div className="currency_item">
                <StyledButton
                  className="basic"
                  color={colors.darkBlue}
                  onClick={() => history.push('/accounts/overview/cash')}
                >
                  <div className="flag cash">
                    <Icon
                      name="money"
                      style={{ fontSize: '1rem', margin: '0' }}
                    />
                  </div>
                  <div style={{ marginLeft: '36px' }}>Incl. Cash</div>
                </StyledButton>
              </div>
            )}
          </CurrencyOption>
        </Feature>
        <Feature name={ACCOUNTS_ADDITIONAL_FEATURES}>
          <MoreActions>
            <div className="trigger">
              <Button
                className={cn('left grey-focus2', {
                  disabled: isNegativePort,
                })}
                icon="ellipsis horizontal"
                color={colors.teal}
                style={{ zIndex: 1, width: '137px', margin: 0 }}
              >
                More
              </Button>
            </div>
            <div className="action-item">
              <ButtonFA
                className="left"
                icon={faSync}
                color={colors.teal}
                onClick={() => {
                  dispatch(ReallocateAccount.resetReallocate());
                  history.push(`/tools/portfolio-reallocate`);
                }}
                style={{
                  backgroundColor: 'white',
                  display: 'block',
                  width: '100%',
                  marginBottom: '5px',
                }}
              >
                Reallocate
              </ButtonFA>
            </div>
          </MoreActions>
        </Feature>
        <Divider fitted />

        {!overview.holdings.length ? (
          <Header
            size="small"
            content="When you add holdings to your accounts, they will be listed here."
          />
        ) : (
          <div>
            <Grid>
              <Grid.Row columns={2}>
                <Grid.Column width={7}>
                  <div style={{ marginTop: '1.3rem' }}>
                    <div style={{ float: 'left', padding: '0 2rem 1rem 0' }}>
                      <Header
                        size="medium"
                        content="Total Accounts Value:"
                        sub
                        style={{ margin: 0, fontWeight: 700 }}
                      />
                      <Value className="main">
                        {`$${this.parseNum(overview.balance)}`}
                        <Flag
                          name={user_region === 'CA' ? 'ca' : 'us'}
                          style={{
                            position: 'relative',
                            bottom: '7px',
                            marginLeft: 7,
                          }}
                        />
                      </Value>
                    </div>

                    <div style={{ float: 'left', padding: '0 0 1rem 0' }}>
                      <Header size="medium" sub style={{ margin: 0 }}>
                        Daily Change
                        <Popup
                          trigger={
                            <Icon
                              name="question circle outline"
                              style={{ fontSize: '1rem', verticalAlign: 'top' }}
                            />
                          }
                          position="right center"
                          content="Of holdings that are matched only"
                        />
                      </Header>
                      <Value>{`${overview.change}%`}</Value>
                    </div>
                  </div>

                  <div style={{ clear: 'both' }}>
                    <Header
                      size="medium"
                      content="Total Holdings Value:"
                      sub
                      style={{ margin: 0 }}
                    />
                    <Value>{`$${this.parseNum(
                      overview.balance - overview.cash
                    )}`}</Value>
                  </div>
                  <div style={{ marginTop: '1.3rem' }}>
                    <Header
                      size="medium"
                      content="Total Cash Value:"
                      sub
                      style={{ margin: 0 }}
                    />
                    <Value>{`$${this.parseNum(overview.cash)}`}</Value>
                  </div>
                </Grid.Column>
                <Grid.Column width={9}>
                  <Segment basic compact>
                    <Header
                      size="medium"
                      textAlign="center"
                      style={{ margin: '0 0 1rem 0' }}
                    >
                      Net Asset Allocation
                      <Header.Subheader>
                        Includes a breakdown of the underlying holdings of
                        balanced and asset allocation funds.
                        <Popup
                          trigger={
                            <Icon
                              name="question circle outline"
                              style={{
                                fontSize: '15px',
                                verticalAlign: 'initial',
                                color: 'dimgrey',
                              }}
                            />
                          }
                          position="top center"
                          content="Analyze and use the Scorecard to drill down to the underlying holdings to i) see the geographical split by asset class, and ii) check fund structure (e.g., some funds use derivatives). You will find this information in Diversification → Asset Class (bottom chart)."
                          wide
                        />
                      </Header.Subheader>
                    </Header>
                    <AssetChart accounts={accountList} overview={overview} />
                  </Segment>
                </Grid.Column>
              </Grid.Row>
            </Grid>
            <HoldingTable
              accountList={accountList}
              totalMarketValue={overview.balance - overview.cash}
              holdings={excludeGicHoldings.slice(0, 10)}
              accountName=""
              title="Top 10 Holdings"
              rate={pwpaExchangeRate}
              user_region={user_region}
            />
            <GicTable
              user_region={user_region}
              gicHoldings={gicHoldings}
              totalMarketValue={overview.balance - overview.cash}
              title="GIC"
              rate={pwpaExchangeRate}
            />
            {excludeGicHoldings.length !== 0 && (
              <P>
                {user_region === 'CA' && overview.currency === 'USD' ? (
                  // eslint-disable-next-line react/jsx-fragments
                  <React.Fragment>
                    For USD accounts, we only have data for U.S.-listed
                    stocks/ETFs, U.S. mutual funds, and Canadian-listed
                    securities denominated in USD.
                    <br />
                    Any other securities such as{' '}
                    <b>private funds, individual bonds, and derivatives</b> will
                    be excluded from the analysis.
                  </React.Fragment>
                ) : user_region === 'CA' ? ( // eslint-disable-next-line react/jsx-fragments
                  <React.Fragment>
                    Currently, we have data for Canadian and US stocks/ETFs,
                    Canadian mutual and segregated funds, and CAD/HISA funds.
                    <Popup
                      trigger={
                        <Icon
                          name="question circle outline"
                          style={{ fontSize: '15px', verticalAlign: 'initial' }}
                        />
                      }
                      position="top center"
                      content="CAD/USD HISA funds are included using the historical rates of a representative bank-owned CAD/USD HISA fund."
                    />
                    <br />
                    Any other securities such as{' '}
                    <b>private funds, individual bonds, and derivatives</b> will
                    be excluded from the analysis.
                  </React.Fragment>
                ) : (
                  'Currently we only have data for U.S. listed stocks and ETFs, U.S. mutual funds, Bitcoin, and Ethereum. Any other security will be excluded from the analysis.'
                )}
              </P>
            )}
            {excludeGicHoldings.length !== 0 && user_region === 'CA' && (
              <P>USD/CAD exchange rate used is {pwpaExchangeRate || 1.3}</P>
            )}
            {hasNegativeWeights ? (
              isNegativePort ? (
                <p
                  style={{
                    color: `${colors.red}`,
                    fontWeight: 'bold',
                    position: 'absolute',
                    top: '4.5rem',
                    right: '2rem',
                  }}
                >
                  <Icon name="warning sign" style={{}} />
                  Analysis will not be performed for net short portfolio(s).
                </p>
              ) : (
                <p
                  style={{
                    color: `${colors.red}`,
                    fontWeight: 'bold',
                    position: 'absolute',
                    top: '4.5rem',
                    right: '2rem',
                  }}
                >
                  <Icon name="warning sign" style={{}} />
                  Short positions will be excluded from the analysis.
                </p>
              )
            ) : null}
          </div>
        )}
      </Segment>
    );
  }
}

Overview.propTypes = {
  history: PropTypes.object.isRequired,
  overview: PropTypes.object,
  dispatch: PropTypes.func,
  accountList: PropTypes.array,
  pwpaExchangeRate: PropTypes.number,
  user_region: PropTypes.string,
};

Overview.defaultProps = {
  overview: {},
  dispatch: () => {},
  accountList: [],
  pwpaExchangeRate: 0,
  user_region: 'CA',
};

export default connect((state) => ({
  overview: state.Accounts.overview,
  accountList: state.Accounts.accountList,
  pwpaExchangeRate: state.Storage['pwpa-exchange-rate'],
  user_region: state.Storage.user_region || 'CA',
}))(Overview);

const Value = styled.div`
  font-size: 1.8rem;
  padding: 7px 0;

  &.main {
    font-size: 2.4rem;
    font-weight: 700;
    color: ${colors.orange};
  }
`;

const MoreActions = styled.div`
  .action-item {
    position: absolute;
    top: 20px;
    right: 2rem;
    padding: 2rem 0 0 0;
    width: 137px;
    opacity: 0;
    visibility: hidden;
    transition: opacity 200ms ease, top 150ms ease;
    z-index: 2;
    &:before {
      content: '';
      position: absolute;
      top: 28px;
      left: 0;
      height: 90px;
      width: 133px;
      background: white;
      border-radius: 4px;
    }
    &:hover {
      opacity: 1;
      visibility: visible;
      top: 40px;
    }
  }
  .trigger {
    z-index: 2;
    width: 137px;
    position: absolute;
    top: 1.6rem;
    right: 2rem;
    cursor: default;
    transition: all 300ms ease;
    &:hover {
      & ~ .action-item {
        opacity: 1;
        visibility: visible;
        top: 40px;
      }
    }
  }
  button {
    display: block;
    margin: 5px auto;
    background-color: white;
  }
`;

const P = styled.p`
  color: rgba(0, 0, 0, 0.5);
  font-style: italic;
  margin: 0.7rem auto;
  max-width: 100%;
  line-height: 1.1rem;
  text-align: center;
`;

const CurrencyOption = styled.div`
  position: absolute;
  top: 1.6rem;
  right: 12.5rem;
  z-index: 3;
  cursor: pointer;
  &:hover {
    & .currency_item {
      opacity: 1;
      visibility: visible;
      top: 18px;
      transition: opacity 200ms ease, top 150ms ease;
    }
  }
  .currency_item {
    position: absolute;
    top: 0px;
    right: 0;
    padding: 2rem 0 0 0;
    opacity: 0;
    visibility: hidden;
  }
`;

const StyledButton = styled.div`
  position: relative;
  border-radius: 4px;
  box-shadow: 0 0 0 1px rgba(34, 36, 38, 0.15) inset;
  font-size: 0.9rem;
  display: inline-block;
  min-height: 1em;
  min-width: 113px;
  height: 36px;
  border: none;
  text-align: center;
  outline: 0;
  transition: all 0.3s ease;
  overflow: hidden;
  color: rgba(0, 0, 0, 0.6);
  background-color: transparent;
  vertical-align: top;
  line-height: 36px !important;
  user-select: none !important;
  margin-top: 5px !important;
  background-color: white;
  &:hover {
    box-shadow: inset 0 0 0 1.5px ${colors.darkBlue} !important;
    color: rgba(0, 0, 0, 0.6);
  }
  .flag {
    height: 100%;
    position: absolute;
    color: white;
    top: 0;
    width: 36px;
    background-color: ${colors.darkBlue};
    padding-right: 36px;
    &:before {
      position: absolute;
      line-height: 1;
      font-size: 1em;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  .cash {
    padding-right: 0px;
  }
`;
